import React, { useEffect, useState } from 'react';
import type { QuestionPage } from 'magical-trails';

export interface ReplyTarget {
  pointCode: string;
  pageIndex: number;
  page: QuestionPage;
  senderName: string;
}

export interface Props {
  /** The question being answered, or null when nobody is waiting on a reply. */
  target: ReplyTarget | null;
  /** More than one question is open: say which one this reply goes to. */
  showTargetStrip: boolean;
  /** Someone is typing: hold the player's reply until they have finished. */
  busy: boolean;
  /** Who is typing, for the idle line. */
  typingName?: string;
  /** The thread waits on a button in it (a photo, an activity), not on text. */
  actionAbove: boolean;
  threadName: string;
  onSend: (target: ReplyTarget, text: string) => void;
}

function plainText(html: string): string {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * The bottom of a thread. It only ever sends an answer to a question page:
 * chips for multiple choice, a text or number field otherwise. With nothing
 * to answer it stays on screen, greyed, the way a real messenger's would —
 * a thread with no composer at all reads as broken.
 */
const Composer: React.FC<Props> = ({
  target,
  showTargetStrip,
  busy,
  typingName,
  actionAbove,
  threadName,
  onSend,
}) => {
  const [text, setText] = useState('');
  const targetKey = target ? `${target.pointCode}:${target.pageIndex}` : '';
  useEffect(() => setText(''), [targetKey]);

  if (!target) {
    return (
      <div className="Phone_composer Phone_composer-idle">
        {/* The typing dots are decoration; this line is what says so. */}
        <p aria-live="polite">
          {busy
            ? `${typingName ?? threadName} is typing…`
            : actionAbove
              ? 'Use the button above to reply'
              : 'Nothing to reply to right now'}
        </p>
      </div>
    );
  }

  const { page } = target;
  const strip = showTargetStrip ? (
    <p className="Phone_replyingTo">
      Replying to <strong>{target.senderName}</strong>:{' '}
      {plainText(page.question)}
    </p>
  ) : null;

  if (page.answerType === 'multipleChoice') {
    const choices = page.choices ?? [];
    return (
      <div className="Phone_composer Phone_composer-chips">
        {strip}
        <div className="Phone_chips" role="group" aria-label="Choose a reply">
          {choices.length >= 2 ? (
            choices.map((choice, index) => (
              <button
                key={`${index}:${choice}`}
                type="button"
                className="Phone_chip"
                onClick={() => onSend(target, choice)}
              >
                {choice}
              </button>
            ))
          ) : (
            // A multiple-choice question with nothing to choose from can't
            // be answered: let the player past rather than strand them.
            <button
              type="button"
              className="Phone_chip"
              onClick={() => onSend(target, page.answer)}
            >
              Continue
            </button>
          )}
        </div>
      </div>
    );
  }

  const send = () => {
    const value = text.trim();
    if (!value) return;
    onSend(target, value);
    setText('');
  };

  return (
    <form
      className="Phone_composer"
      onSubmit={(event) => {
        event.preventDefault();
        send();
      }}
    >
      {strip}
      <div className="Phone_composerRow">
        <label className="sr-only" htmlFor="phone-composer-input">
          Your reply to {target.senderName}
        </label>
        <input
          id="phone-composer-input"
          className="Phone_composerInput"
          type="text"
          inputMode={page.answerType === 'number' ? 'decimal' : 'text'}
          autoComplete="off"
          autoCapitalize="off"
          enterKeyHint="send"
          placeholder={
            page.answerType === 'number' ? 'Type a number' : 'Type your reply'
          }
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
        <button
          type="submit"
          className="Phone_send"
          disabled={!text.trim()}
          aria-label="Send"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M4.5 12 19.5 5l-4 14-3.5-5.5L4.5 12Z"
              fill="currentColor"
            />
          </svg>
        </button>
      </div>
    </form>
  );
};

export default Composer;
